<template>
  <dialog ref="modalRef" class="modal" @close="handleDialogClose">
    <div class="modal-content">
      <div class="modal-header" @mousedown="startDrag">
        <h2>{{ title }}</h2>
        <BaseIconButton icon="times" type="default" @click="$emit('close')" />
      </div>

      <div class="modal-body">
        <slot />
      </div>

      <div class="modal-footer">
        <slot name="footer">
          <BaseButton icon="times" type="default" @click="$emit('close')">
            Lukk
          </BaseButton>
        </slot>
      </div>
    </div>
    <BaseToast />
  </dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import BaseIconButton from '@/components/BaseComponents/BaseIconButton.vue'
import BaseButton from '@/components/BaseComponents/BaseButton.vue'
import BaseToast from '@/components/BaseComponents/BaseToast.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  fullHeight: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])
const modalRef = ref(null)

function handleDialogClose () {
  if (props.modelValue) {
    emit('update:modelValue', false)
    emit('close')
  }
}

function centerDialog () {
  const rect = modalRef.value.getBoundingClientRect();
  const left = (window.innerWidth - rect.width) / 2;
  const top = (window.innerHeight - rect.height) / 2;

  modalRef.value.style.left = `${left}px`;
  modalRef.value.style.top = `${top}px`;
}

watch(() => props.modelValue, (newValue) => {
  if (newValue && modalRef.value) {
    modalRef.value.showModal();
    centerDialog(); // Midtstill dialogen når den vises
  } else if (modalRef.value) {
    modalRef.value.close();
  }
});

let startX, startY, initialLeft, initialTop, initialWidth, initialHeight;

function startDrag (event) {
  startX = event.clientX;
  startY = event.clientY;

  const rect = modalRef.value.getBoundingClientRect();
  initialLeft = rect.left;
  initialTop = rect.top;
  initialWidth = rect.width;
  initialHeight = rect.height;

  console.log('drag start', { initialLeft, initialTop, startX, startY, initialWidth, initialHeight });

  document.addEventListener('mousemove', drag);
  document.addEventListener('mouseup', stopDrag);
}

function drag (event) {
  const deltaX = event.clientX - startX;
  const deltaY = event.clientY - startY;

  let newLeft = initialLeft + deltaX;
  let newTop = initialTop + deltaY;

  // Beregn maksimale grenser
  const maxLeft = window.innerWidth - initialWidth;
  const maxTop = window.innerHeight - initialHeight;

  // Begrens dialogens posisjon til viewport
  newLeft = Math.max(0, Math.min(newLeft, maxLeft));
  newTop = Math.max(0, Math.min(newTop, maxTop));

  modalRef.value.style.left = `${newLeft}px`;
  modalRef.value.style.top = `${newTop}px`;
}

function stopDrag () {
  console.log('drag stop', { startX, startY, initialLeft, initialTop, initialWidth, initialHeight });
  document.removeEventListener('mousemove', drag);
  document.removeEventListener('mouseup', stopDrag);
}
</script>

<style scoped>
.modal {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  min-width: 18vw;
  max-width: 90vw;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  margin: 0;
  overflow: hidden;
}

.modal::backdrop {
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  cursor: move;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  user-select: none;
}

.modal-body {
  display: flex;
  flex-direction: column;
  padding: 0.5em 1em;
  overflow-y: auto;
  max-height: calc(90vh - 72px - 3rem);
  flex: 1;
}

.modal-footer {
  padding: 0.5rem 1rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
